import { readonly, ref } from 'vue'
import en from './locales/en.mjs'
import zh from './locales/zh.mjs'
import { defaultNodes, defaultLinks, defaultFacets } from './document.mjs'
import { defaultMotifs } from './motifs.mjs'

const storageKey = 'argumotif.locale'
const current = ref('zh')
export const locale = readonly(current)

// Locale is a display preference, independent of workspace data and schema versions.
export function setLocale(value) {
  current.value = value === 'en' ? 'en' : 'zh'
  if (typeof document !== 'undefined') document.documentElement.lang = current.value === 'en' ? 'en' : 'zh-CN'
  try {
    localStorage.setItem(storageKey, current.value)
  } catch {
    /* Storage may be unavailable. */
  }
}
export function initializeLocale() {
  let saved
  try {
    saved = localStorage.getItem(storageKey)
  } catch {
    /* Keep the Chinese default. */
  }
  setLocale(saved)
}
export function t(key, params = {}) {
  const dictionary = current.value === 'en' ? en : zh
  const value =
    dictionary[key] ??
    dictionary.__missing__ ??
    (current.value === 'en' ? 'Missing interface text' : '界面文案缺失')
  return String(value ?? '').replace(/\{(\w+)\}/g, (match, name) =>
    Object.hasOwn(params, name) ? params[name] : match,
  )
}

const definitions = {
  claim: 'A claim or conclusion proposed by the author that requires supporting evidence.',
  evidence: 'Historical sources, quotations, facts or examples used to support or refute a claim.',
  context:
    'Historical circumstances, background or conceptual definitions needed to understand the argument.',
  analysis: 'Analysis and reasoning about sources, causal mechanisms or competing interpretations.',
  focus: 'The research question, subject and scope at the center of the discussion.',
  support: 'The source node provides evidence for the claim in the target node.',
  elaborate: 'The source node develops, explains or specifies the target node.',
  contextualize: 'The source node provides historical or conceptual background for the target node.',
  focus_on: 'The source node directs the discussion toward the research question in the target node.',
  person: 'Historical figures and researchers mentioned in an argument unit.',
  location: 'Geographic regions and places mentioned in an argument unit.',
  keyword: 'Core concepts, periods and groups in the argument.',
}
const defaults = { node: defaultNodes, link: defaultLinks, facet: defaultFacets, motif: defaultMotifs }
export function schemaText(item, field = 'name', kind = 'node') {
  if (!item) return ''
  const value = item[field] ?? ''
  const original = defaults[kind]?.find((s) =>
    item.legacyId ? s.legacyId === item.legacyId : s.id === item.id,
  )
  if (field === 'name') {
    const builtIn = original && [original.name, original.english].filter(Boolean).includes(value)
    if (builtIn) return current.value === 'en' ? original.english || original.name : original.name
    return current.value === 'en' ? item.english || value : value
  }
  if (field === 'definition' && original) {
    const translated = definitions[original.legacyId ?? original.id]
    if ([original.definition, translated].filter(Boolean).includes(value))
      return current.value === 'en' ? translated || original.definition : original.definition
  }
  // User-authored document and schema content is never machine-translated.
  return value
}
export function chapterTitle(chapter, doc) {
  if (!chapter) return ''
  const generated =
    chapter.title === '正文' &&
    !doc.blocks.some((block) => block.kind === 'heading' && block.chapterId === chapter.id)
  return generated ? t('正文') : chapter.title
}

const countKeys = {
  段落: '计数：段落',
  章节: '计数：章节',
  论证单元: '计数：论证单元',
  条关系: '计数：关系',
  个论证单元: '计数：论证单元',
  句: '计数：句子',
  个匹配: '计数：匹配',
}
export function countLabel(count, noun) {
  const key = countKeys[noun]
  if (!key) return String(count)
  const suffix = current.value === 'en' && count !== 1 ? (noun === '个匹配' ? 'es' : 's') : ''
  return t(key, { count, suffix })
}

export function yearLabel(year) {
  return year < 0 ? t('公元前年份', { year: Math.abs(year) }) : String(year)
}

// Legacy API records store human-readable Chinese messages. Translate only this
// system-message boundary, never document content or user-supplied schema fields.
const patterns = [
  [
    /^模型服务请求失败（(.+)），请核对 \.env 的地址、模型权限和 key。$/,
    (_, code) => `Model service request failed (${code}). Check the .env URL, model permissions and key.`,
  ],
  [
    /^分析失败：(.+)。查看本地任务记录后重试。$/,
    (_, code) => `Analysis failed: ${code}. Check the local task record and retry.`,
  ],
  [/^(已中断|分析失败) · (.+)$/, (_, state, stage) => `${t(state)} · ${message(stage)}`],
  [/^重置失败：(.+)$/, (_, detail) => `Reset failed: ${message(detail)}`],
  [/^导入失败：(.+)$/, (_, detail) => `Import failed: ${message(detail)}`],
  [/^无法读取文本 \((.+)\)$/, (_, code) => `Could not read text (${code})`],
  [
    /^请填写 \.env 并重启后端：(.+)$/,
    (_, fields) => `Complete .env and restart the backend: ${fields.replaceAll('、', ', ')}`,
  ],
  [
    /^段落 (.+) 的类型、章节或句子不合法。$/,
    (_, id) => `Paragraph ${id} has an invalid type, chapter or sentences.`,
  ],
  [
    /^(.+) 的句子必须完整还原段落文本。$/,
    (_, id) => `The sentences of ${id} must reproduce the entire paragraph text.`,
  ],
  [/^(.+) 的类型或句子范围不合法。$/, (_, id) => `${id} has an invalid type or sentence range.`],
  [
    /^(.+) 必须引用同一段内连续的句子。$/,
    (_, id) => `${id} must reference consecutive sentences in one paragraph.`,
  ],
  [/^(.+) 的论证单元范围重叠。$/, (_, id) => `${id} contains overlapping argument units.`],
  [/^(.+) 存在未归属论证单元的句子。$/, (_, id) => `${id} has sentences not assigned to argument units.`],
  [/^关系 (.+) 的端点或类型无效。$/, (_, id) => `Relation ${id} has invalid endpoints or type.`],
  [
    /^关系 (.+) 不能跨越段落和单元层级。$/,
    (_, id) => `Relation ${id} cannot cross paragraph and unit levels.`,
  ],
  [/^(.+) 必须返回 JSON 对象$/, (_, agent) => `${agent} must return a JSON object`],
  [/^(.+) 必须是数组$/, (_, key) => `${key} must be an array`],
  [/^(.+) 的 ID 无效或重复$/, (_, key) => `${key} has invalid or duplicate IDs`],
  [
    /^(聊天|Embedding)服务返回了(HTML 网页|文本)，不是模型 JSON。请检查 BASE_URL 的 API 路径（通常以 \/v1 结尾）。$/,
    (_, service, kind) =>
      `${service === '聊天' ? 'Chat' : service} returned ${kind === '文本' ? 'text' : 'an HTML page'} instead of model JSON. Check the BASE_URL API path (usually ending in /v1).`,
  ],
  [/^(evidence\[\d+\]) 必须包含 node_id 和 quote$/, (_, key) => `${key} must contain node_id and quote`],
  [
    /^(evidence\[\d+\]\.node_id) 必须是当前关系的一个端点$/,
    (_, key) => `${key} must be an endpoint of this relation`,
  ],
  [
    /^(evidence\[\d+\]\.quote) 必须是非空原文引用$/,
    (_, key) => `${key} must be a non-empty source quotation`,
  ],
  [
    /^(evidence\[\d+\]\.quote) 不是 (\S+) (原文|段落摘要)中的连续片段，请将多处引用拆成独立条目$/,
    (_, key, id, basis) =>
      `${key} is not a contiguous passage of ${id}'s ${basis === '原文' ? 'source text' : 'paragraph summary'}. Split separate quotations into independent entries`,
  ],
]
export function message(value) {
  if (!value || current.value !== 'en') return value || ''
  if (Object.hasOwn(en, value)) return en[value]
  for (const [pattern, replace] of patterns) if (pattern.test(value)) return value.replace(pattern, replace)
  return /\p{Script=Han}/u.test(value)
    ? 'The operation could not be completed. Check the local task record for details.'
    : value
}
